import {
  getManifestI18n,
  isLocaleSupportedByManifest,
  isUrlLocaleSegment,
  localeToUrlSegment,
  normalizeManifestLocale,
  parseUrlLocaleSegment,
} from "../../intl/manifestI18n";
import { getI18nRollout } from "../../intl/i18nRollout.js";

export function runtimeBasePath(slug, locale = null, manifest = null) {
  const rollout = manifest ? getI18nRollout(manifest) : { localizedRoutesEnabled: true };
  const manifestI18n = getManifestI18n(manifest);
  const normalizedLocale = locale ? normalizeManifestLocale(locale) : null;
  const isDefaultLocale =
    normalizedLocale && normalizedLocale === manifestI18n.defaultLocale;
  const useLocale =
    rollout.localizedRoutesEnabled !== false &&
    manifestI18n.supportedLocales.length > 1 &&
    normalizedLocale &&
    !isDefaultLocale;
  const segment = useLocale ? localeToUrlSegment(normalizedLocale, manifestI18n) : null;
  return segment ? `/v/${slug}/${segment}` : `/v/${slug}`;
}

export function buildLocalizedRuntimePath(slug, locale, subpath = "", search = "", manifest = null) {
  const base = runtimeBasePath(slug, locale, manifest);
  const normalizedSubpath =
    subpath && subpath !== "/"
      ? subpath.startsWith("/")
        ? subpath
        : `/${subpath}`
      : "";
  const href = `${base}${normalizedSubpath}`;
  if (!search) {
    return href;
  }
  const query = search.startsWith("?") ? search : `?${search}`;
  return `${href}${query}`;
}

export function parseClientViewPathname(pathname, slug, manifest = null) {
  const manifestI18n = getManifestI18n(manifest);
  const rollout = getI18nRollout(manifest);
  const basePrefix = `/v/${slug}`;
  const remainder = pathname.startsWith(basePrefix)
    ? pathname.slice(basePrefix.length)
    : pathname;
  const trimmed = remainder.replace(/^\//, "");
  const parts = trimmed ? trimmed.split("/") : [];
  const first = parts[0] || null;
  const parsedLocale = parseUrlLocaleSegment(first, manifestI18n);

  if (parsedLocale && isUrlLocaleSegment(first, manifestI18n) && rollout.localizedRoutesEnabled !== false) {
    const supported = isLocaleSupportedByManifest(parsedLocale, manifestI18n);
    const innerParts = parts.slice(1);
    const relativePathname = innerParts.length ? `/${innerParts.join("/")}` : "/";
    return {
      urlLocale: supported ? parsedLocale : null,
      localeSegment: first,
      unsupportedLocaleSegment: supported ? null : first,
      relativePathname,
      manifestI18n,
    };
  }

  return {
    urlLocale: null,
    localeSegment: null,
    unsupportedLocaleSegment: null,
    relativePathname: remainder || "/",
    manifestI18n,
  };
}

export function switchLocaleInPathname(pathname, search, slug, manifest, nextLocale) {
  const parsed = parseClientViewPathname(pathname, slug, manifest);
  const targetSegment = localeToUrlSegment(nextLocale);
  if (!targetSegment || !isLocaleSupportedByManifest(nextLocale, parsed.manifestI18n)) {
    return null;
  }
  return buildLocalizedRuntimePath(slug, nextLocale, parsed.relativePathname, search, manifest);
}

export function buildAlternateLocaleUrls(pathname, search, slug, manifest) {
  const parsed = parseClientViewPathname(pathname, slug, manifest);
  const rollout = getI18nRollout(manifest);
  if (rollout.localizedRoutesEnabled === false || parsed.manifestI18n.supportedLocales.length <= 1) {
    return [];
  }
  const searchSuffix = search || "";
  const alternates = parsed.manifestI18n.supportedLocales.map((locale) => ({
    hreflang: locale,
    href: buildLocalizedRuntimePath(slug, locale, parsed.relativePathname, searchSuffix, manifest),
  }));
  if (parsed.manifestI18n.supportedLocales.length > 1) {
    alternates.push({
      hreflang: "x-default",
      href: buildLocalizedRuntimePath(
        slug,
        parsed.manifestI18n.defaultLocale,
        parsed.relativePathname,
        searchSuffix,
        manifest
      ),
    });
  }
  return alternates;
}
